/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import PhoneInput from 'react-phone-number-input';
import { useSelector, useDispatch } from 'react-redux';
import { isValidPhoneNumber } from 'react-phone-number-input';
import {
  AlertCheckCircleFill,
  ArrowChevronLeft,
  ArrowTrendDownFill,
  Eye,
  EyeOff,
  ShapeCircle
} from '@jda/lui-common-icon-library-mui5';
import { LuiCard, LuiSelect, LuiSelectItemEvent, LuiModal } from '@jda/lui-common-component-library-mui5';
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Backdrop,
  CircularProgress
} from '@mui/material';
import { useStyles } from './styles';
import Loader from '../../shared/Loader';
import FormDialog from '../../shared/Dialog/FormDialog';
import { getAllIdps, selectLocationIdpId } from '../../store/user/userSelector';
import { createNewDirectoryUser } from '../../services/user';
import { IErrorMessage } from '../../models/Common/ErrorMessage';
import interpolateString from '../../utility/stringInterpolation';
// import useWindowDimensions from '../../utility/useWindowDimensions';
import { ErrorMessage } from '../../shared/ErrorMessage/ErrorMessage';
import UserPasswordDisplay from '../UserManagement/UserPasswordDisplay';
import getTextFieldHelperProps from '../UserManagement/textFieldHelperPropGetter';
import { setSearchUser } from '../../store/user/userActionCreator';
import { fetchIdps } from '../../store/user/users';
import { LABELS, MESSAGES, PREFERRED_MFA_LIST, PASSWORD_POLICY_ARRAY } from './constant';
import { selectUserFormData } from '../../store/user/saveUser/saveUserSelector';
import { setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';
import {
  KEY_NAMES,
  LOCAL_IDP_TYPE,
  NATIVE_IDP_TYPE,
  USER_FORM_FIELDS,
  FEDERATED_USER_FIELDS,
  LOCAL_USER_FIELDS,
  NATIVE_USER_FIELDS
} from '../UserManagement/constant';
import { compareByProperty, isValidEmail, isValidUserName } from '../../utility/commonUtility';
import {
  resetUserFormData,
  updateUserFormData,
  updateUserFormDataField
} from '../../store/user/saveUser/saveUserActionCreator';
import SnackbarAlert from '../../shared/SnackbarAlert';
import { selectAppLocation } from '../../store/appLocation/appLocationSelector';
import { getAllPasswordPolicy, getIdpById, getPasswordPolicyRule } from '../../services/setting';
import passwordDictionary from '../../shared/passwordDictionary/passwordDictionary.json';
import './phone-input.css';

const AddUser = () => {
  const { classes } = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const selectGetAllIdps = useSelector(getAllIdps);
  const userFormValues = useSelector(selectUserFormData);
  const appLocation = useSelector(selectAppLocation);
  const locationIdpId = useSelector(selectLocationIdpId);

  const formDefaultValues = {
    givenName: '',
    lastName: '',
    displayName: ''
  };

  const [showLoader, showHideLoader] = useState(false);
  const [openModal, openCloseModal] = useState(false);
  const [userPasswordText, updateUserPasswordText] = useState('');
  // const [serverErrorMessage, updateServerErrorMessage] = useState('');
  const [errors, setErrors] = useState<IErrorMessage>({ message: '' });
  const [formValues, updateFormValues] = useState<any>(formDefaultValues);
  const [locationIdp, setLocationIdp] = useState<any>();
  const [displayCopyPasswordOption, enableDisplayCopyPasswordOption] = useState(false);
  const [backdropOpen, setBackDropOpen] = useState<boolean>(false);
  const [emailAsUsername, setEmailAsUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [defaultPasswordPolicyRule, setDefaultPasswordPolicyRule] = React.useState<any>(null);
  const [isMinMax, setIsMinMax] = useState(false);
  const [isIncludeNumbers, setIsIncludeNumbers] = useState(false);
  const [isLettersInMixedCase, setIsLettersInMixedCase] = useState(false);
  const [isIncludeSpecialCharacters, setIsIncludeSpecialCharacters] = useState(false);
  const [isMaxConsecutiveLetters, setIsMaxConsecutiveLetters] = useState(false);
  const [isNotCommonPassword, setIsNotCommonPassword] = useState(false);
  const [emailAsUsernameInputValue, setEmailAsUsernameInputValue] = useState<string>('');
  const [copiedEmailAsUsernameValue, setCopiedEmailAsUsernameValue] = useState<string>('');
  const commonPasswordsSet = new Set(passwordDictionary);

  const handleClose = () => {
    openCloseModal(!openModal);
  };

  const getFormFieldValue = (fieldName: string) => {
    return userFormValues[fieldName] && userFormValues[fieldName].value
      ? userFormValues[fieldName].value
      : fieldName === USER_FORM_FIELDS.IS_MFA_ENABLED
        ? false
        : '';
  };

  const selectedIdpId = getFormFieldValue(USER_FORM_FIELDS.IDP_ID);

  const idpList = useMemo(() => {
    const idps = !appLocation ? [...selectGetAllIdps].sort(compareByProperty()) : locationIdp ? [locationIdp] : [];

    return idps.map(idp => ({ ...idp, key: idp.id, displayName: idp.name }));
  }, [appLocation, selectGetAllIdps, locationIdp]);

  const selectedIdp = idpList.find((idp: { id: string }) => idp.id === selectedIdpId);

  const isLocalUser = selectedIdp?.idpType === LOCAL_IDP_TYPE;
  const isNativeUser = selectedIdp?.idpType === NATIVE_IDP_TYPE || selectedIdp?.type === 'NATIVE';

  const idpTypeChangeHandler = (e: LuiSelectItemEvent) => {
    dispatch(
      updateUserFormDataField(USER_FORM_FIELDS.IDP_ID, { value: e.target.value.key, error: false, errorHelperText: '' })
    );
    dispatch(
      updateUserFormDataField(USER_FORM_FIELDS.USER_NAME, {
        ...userFormValues[USER_FORM_FIELDS.USER_NAME],
        error: false,
        errorHelperText: ''
      })
    );
  };

  useEffect(() => {
    if (appLocation && locationIdpId) {
      getIdpById(locationIdpId).then(setLocationIdp);
    }
  }, [appLocation, locationIdpId]);

  useEffect(() => {
    if (locationIdpId) {
      dispatch(
        updateUserFormDataField(USER_FORM_FIELDS.IDP_ID, {
          value: locationIdpId,
          error: false,
          errorHelperText: ''
        })
      );
    }
  }, [locationIdpId, dispatch]);

  useEffect(() => {
    setErrors({ message: '' });
    fetchIdps(dispatch).catch((_error: any) => {
      const errorMessage = {
        message: MESSAGES.ERROR_IN_FETCHING_IDENTITY_PROVIDER_NAME
      };
      setErrors(errorMessage);
    });
  }, [dispatch]);

  const getDefaultPasswordPolicyRule = useCallback(async () => {
    if (isNativeUser && selectedIdp) {
      try {
        const currentLocationName = appLocation?.name || '';
        const defaultPasswordPolicy = await getAllPasswordPolicy().then((res: any) => {
          let idpPolicy = res.find((policy: any) => policy.idpsNames.length > 0);
          res.forEach((policy: any) => {
            policy.idpsNames.length &&
              policy.idpsNames.forEach((locationIdpName: any) => {
                if (locationIdpName.includes(currentLocationName)) {
                  idpPolicy = policy;
                }
              });
          });
          const defaultPolicy = res.find((policy: any) => policy.isDefault);
          return idpPolicy || defaultPolicy;
        });

        const defaultPasswordPolicyRule = await getPasswordPolicyRule(defaultPasswordPolicy.id);

        setDefaultPasswordPolicyRule(defaultPasswordPolicyRule);
      } catch (error) {
        displaySnackbar(MESSAGES.PASSOWORD_POLICY_ERROR, 'error');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdp, isNativeUser, appLocation]);

  useEffect(() => {
    getDefaultPasswordPolicyRule();
  }, [getDefaultPasswordPolicyRule]);

  useEffect(() => {
    return () => {
      dispatch(resetUserFormData());
    };
  }, [dispatch]);

  const getSelectedItem = (value: string, key: string, itemList: any) => {
    const selItem = itemList.filter((item: any) => {
      return item[key] === value;
    });
    return Array.isArray(selItem) && selItem.length > 0 ? selItem[0] : '';
  };

  const updateFormFields = (fieldName: string, value: any) => {
    if (fieldName === 'password' && defaultPasswordPolicyRule) {
      const tempState = {
        isMinMax: true,
        isIncludeNumbers: true,
        isIncludeSpecialCharacters: true,
        isLettersInMixedCase: true,
        isMaxConsecutiveLetters: true,
        isNotCommonPassword: true
      };

      if (defaultPasswordPolicyRule.minLength && defaultPasswordPolicyRule.maxLength) {
        tempState.isMinMax =
          value.length >= defaultPasswordPolicyRule.minLength && value.length <= defaultPasswordPolicyRule.maxLength;
      }

      if (defaultPasswordPolicyRule.includeNumbers) {
        tempState.isIncludeNumbers = /[0-9]/.test(value);
      }

      if (defaultPasswordPolicyRule.includeSpecialCharacters) {
        tempState.isIncludeSpecialCharacters = /[!@#$%^&*]/.test(value);
      }

      if (defaultPasswordPolicyRule.lettersInMixedCase) {
        tempState.isLettersInMixedCase = /^(?=.*[a-z])(?=.*[A-Z])/.test(value);
      }

      if (defaultPasswordPolicyRule.maxConsecutiveLetters > 0) {
        tempState.isMaxConsecutiveLetters = !new RegExp(
          `(.)\\1{${defaultPasswordPolicyRule.maxConsecutiveLetters},}`
        ).test(value);
      }

      if (defaultPasswordPolicyRule.excludePasswordsInDictionary) {
        tempState.isNotCommonPassword = !commonPasswordsSet.has(value);
      }

      setIsMinMax(tempState.isMinMax);
      setIsIncludeNumbers(tempState.isIncludeNumbers);
      setIsIncludeSpecialCharacters(tempState.isIncludeSpecialCharacters);
      setIsLettersInMixedCase(tempState.isLettersInMixedCase);
      setIsMaxConsecutiveLetters(tempState.isMaxConsecutiveLetters);
      setIsNotCommonPassword(tempState.isNotCommonPassword);

      const allValid = Object.values(tempState).every(Boolean);

      dispatch(
        updateUserFormDataField(fieldName, {
          value,
          error: !allValid,
          errorHelperText: allValid ? '' : MESSAGES.PASSWORD_REQUIRED
        })
      );
    } else {
      updateFormValues({ ...formValues, [fieldName]: value });
      dispatch(updateUserFormDataField(fieldName, { value, error: false, errorHelperText: '' }));
    }
  };

  const isPreRegistrationEnabled = getFormFieldValue(USER_FORM_FIELDS.PRE_REGISTRATION);

  const validateRequired = () => {
    let formValid = true;
    let newData = JSON.parse(JSON.stringify(userFormValues));

    const emailValue: string = getFormFieldValue(USER_FORM_FIELDS.EMAIL).trim();
    if ((isLocalUser || (isNativeUser && isPreRegistrationEnabled)) && emailValue === '') {
      newData[USER_FORM_FIELDS.EMAIL].error = true;
      newData[USER_FORM_FIELDS.EMAIL].errorHelperText = MESSAGES.EMAIL_REQUIRED;
      formValid = false;
    } else if (emailValue !== '' && !isValidEmail(emailValue)) {
      newData[USER_FORM_FIELDS.EMAIL].error = true;
      newData[USER_FORM_FIELDS.EMAIL].errorHelperText = MESSAGES.EMAIL_INVALID;
      formValid = false;
    }

    if (isLocalUser && getFormFieldValue(USER_FORM_FIELDS.DISPLAY_NAME) === '') {
      newData[USER_FORM_FIELDS.DISPLAY_NAME].error = true;
      newData[USER_FORM_FIELDS.DISPLAY_NAME].errorHelperText = MESSAGES.DISPLAY_NAME_REQUIRED;
      formValid = false;
    }

    if (isLocalUser && !isValidUserName(getFormFieldValue(USER_FORM_FIELDS.USER_NAME))) {
      newData[USER_FORM_FIELDS.USER_NAME].error = true;
      newData[USER_FORM_FIELDS.USER_NAME].errorHelperText = MESSAGES.LOCAL_USER_NAME_INVALID;
      formValid = false;
    } else if (!emailAsUsername && getFormFieldValue(USER_FORM_FIELDS.USER_NAME) === '') {
      newData[USER_FORM_FIELDS.USER_NAME].error = true;
      newData[USER_FORM_FIELDS.USER_NAME].errorHelperText = MESSAGES.USER_NAME_REQUIRED;
      formValid = false;
    }

    if (
      isNativeUser &&
      !isPreRegistrationEnabled &&
      (getFormFieldValue(USER_FORM_FIELDS.PASSWORD) === '' ||
        !isMinMax ||
        !isIncludeNumbers ||
        !isIncludeSpecialCharacters ||
        !isLettersInMixedCase ||
        !isMaxConsecutiveLetters ||
        !isNotCommonPassword)
    ) {
      newData[USER_FORM_FIELDS.PASSWORD].error = true;
      newData[USER_FORM_FIELDS.PASSWORD].errorHelperText = MESSAGES.PASSWORD_REQUIRED;
      formValid = false;
    }

    if (getFormFieldValue(USER_FORM_FIELDS.IDP_ID) === '') {
      newData[USER_FORM_FIELDS.IDP_ID].error = true;
      newData[USER_FORM_FIELDS.IDP_ID].errorHelperText = MESSAGES.IDP_REQUIRED;
      formValid = false;
    }

    if (isLocalUser) {
      const isMfaEnabled = getFormFieldValue(USER_FORM_FIELDS.IS_MFA_ENABLED) === true;
      const preferredMfa = getFormFieldValue(USER_FORM_FIELDS.PREFERRED_MFA);
      const mobilePhoneValue: string = getFormFieldValue(USER_FORM_FIELDS.MOBILE_PHONE);

      if (mobilePhoneValue) {
        if (!isValidPhoneNumber(mobilePhoneValue)) {
          newData[USER_FORM_FIELDS.MOBILE_PHONE].error = true;
          newData[USER_FORM_FIELDS.MOBILE_PHONE].errorHelperText = MESSAGES.MOBILE_NUMBER_INVALID;
          formValid = false;
        }
      } else if (isMfaEnabled === true && preferredMfa === KEY_NAMES.PREFERRED_MFA_PHONE) {
        newData[USER_FORM_FIELDS.MOBILE_PHONE].error = true;
        newData[USER_FORM_FIELDS.MOBILE_PHONE].errorHelperText = MESSAGES.MOBILE_NUMBER_REQUIRED;
        formValid = false;
      }

      if (isMfaEnabled === true && preferredMfa === '') {
        newData[USER_FORM_FIELDS.PREFERRED_MFA].error = true;
        newData[USER_FORM_FIELDS.PREFERRED_MFA].errorHelperText = MESSAGES.PREFERRED_MFA_REQUIRED;
        formValid = false;
      }
    }

    if (!formValid) {
      dispatch(updateUserFormData(newData));
    }

    return formValid;
  };

  const getUserDetailsPayload = () => {
    const fields = isNativeUser ? NATIVE_USER_FIELDS : isLocalUser ? LOCAL_USER_FIELDS : FEDERATED_USER_FIELDS;

    const userPayloadObj: any = {};
    fields.forEach((field: any) => {
      const value =
        typeof userFormValues[field].value === 'boolean'
          ? userFormValues[field].value
          : userFormValues[field].value.trim();
      if (isLocalUser || userFormValues[field].value) {
        userPayloadObj[field] = value;
      }
    });
    if (appLocation?.name) {
      userPayloadObj['idpId'] = selectGetAllIdps.find((idp: any) => idp.idpType === 'Native')?.id || '';
    }
    if (emailAsUsername) {
      userPayloadObj[USER_FORM_FIELDS.USER_NAME] = userPayloadObj[USER_FORM_FIELDS.EMAIL];
    }

    return userPayloadObj;
  };

  const displaySnackbar = (message: string, severity: 'success' | 'error') => {
    dispatch(
      setSnackBarMessage({
        showSnackbar: true,
        autoHideDuration: 5000,
        message: message,
        severity: severity
      })
    );
  };

  const getFormFieldErrorStatus = (fieldName: string) => {
    return userFormValues[fieldName] && userFormValues[fieldName].error ? true : false;
  };

  const getFormFieldErrorHelperText = (fieldName: string) => {
    return userFormValues[fieldName] && userFormValues[fieldName].errorHelperText
      ? userFormValues[fieldName].errorHelperText
      : '';
  };

  const saveUserDetails = () => {
    setBackDropOpen(true);
    enableDisplayCopyPasswordOption(false);
    if (validateRequired()) {
      const payload: any = getUserDetailsPayload();
      // PORTALV2 use user machine service to create user for all stacks 
      const request = createNewDirectoryUser(payload, appLocation?.name);

      request.then(response => {
        if (response) {
          showHideLoader(false);
          displaySnackbar(interpolateString([response.userName], MESSAGES.USER_CREATE_SUCCESS), 'success');
          dispatch(setSearchUser(response.userName));
          if (response.password) {
            setBackDropOpen(false);
            openCloseModal(true);
            updateUserPasswordText(response.password);
            enableDisplayCopyPasswordOption(true);
          } else {
            history.goBack();
          }
        }
      }).catch((_error: any) => {
        showHideLoader(false);
        let errorResObj = _error?.response?.data?.message || _error?.response?.data?.ErrorMessage || _error;
        let errorMessage = MESSAGES.USER_CREATE_ERROR;
        if (errorResObj.validationErrors) {
          const validationErrors = errorResObj.validationErrors.map((err: any) => `${err.message}`);
          errorMessage += `${validationErrors.join()}`;
        } else {
          let jsonData = { message: errorResObj };
          const jsonMatch = String(errorResObj).match(/\{.*?\}/);
          if (jsonMatch && jsonMatch[0]) {
            jsonData = JSON.parse(jsonMatch[0]);
          }
          errorMessage += jsonData ? jsonData.message : '';
        }
        displaySnackbar(errorMessage, 'error');
      }).finally(() => {
        setBackDropOpen(false);
      });
    } else {
      setBackDropOpen(false);
    }
  };

  const validateFormField = (fieldName: string) => {
    const value = userFormValues[fieldName] && userFormValues[fieldName].value ? userFormValues[fieldName].value : '';
    if (value !== '') {
      switch (fieldName) {
        case USER_FORM_FIELDS.EMAIL:
          if (!isValidEmail(value)) {
            dispatch(
              updateUserFormDataField(fieldName, { value: value, error: true, errorHelperText: MESSAGES.EMAIL_INVALID })
            );
          }
          break;
        case USER_FORM_FIELDS.USER_NAME:
          if (isLocalUser && !isValidUserName(value)) {
            dispatch(
              updateUserFormDataField(fieldName, {
                value: value,
                error: true,
                errorHelperText: MESSAGES.LOCAL_USER_NAME_INVALID
              })
            );
          }
          break;
      }
    }
  };
  const getUserSignInDetails = () => (
    <Grid container spacing={1}>
      <Grid item xs={4}>
        <Box className={classes.contentContainer}>
          {emailAsUsername ? (
            <FormControl fullWidth>
              <TextField
                {...getTextFieldHelperProps({
                  required: true,
                  errorHelperText: getFormFieldErrorHelperText(USER_FORM_FIELDS.USER_NAME),
                  isNativeUser
                })}
                fullWidth
                id="userName"
                variant="standard"
                name="userName"
                label={LABELS.USER_NAME}
                data-testid={'user-name'}
                className={classes.textInput}
                inputProps={{ maxLength: 100 }}
                value={copiedEmailAsUsernameValue}
                disabled={true}
              />
            </FormControl>
          ) : (
            <TextField
              {...getTextFieldHelperProps({
                required: true,
                errorHelperText: getFormFieldErrorHelperText(USER_FORM_FIELDS.USER_NAME),
                isNativeUser
              })}
              fullWidth
              id="userName"
              variant="standard"
              name="userName"
              label={LABELS.USER_NAME}
              data-testid={'user-name'}
              className={classes.textInput}
              inputProps={{ maxLength: 100 }}
              onBlur={() => validateFormField(USER_FORM_FIELDS.USER_NAME)}
              onChange={(e: any) => updateFormFields('userName', e.target.value)}
            />
          )}
        </Box>
      </Grid>
      {isNativeUser && (
        <>
          <Grid item xs={4}>
            <Box className={classes.passwordContainer}>
              <TextField
                {...getTextFieldHelperProps({
                  required: true,
                  disabled: isPreRegistrationEnabled,
                  errorHelperText: getFormFieldErrorHelperText(USER_FORM_FIELDS.PASSWORD),
                  isNativeUser
                })}
                fullWidth
                variant="standard"
                id="password"
                name="password"
                label={LABELS.PASSWORD}
                type={showPassword ? 'text' : 'password'}
                data-testid={'password-test-id'}
                className={classes.textInput}
                inputProps={{ maxLength: 100 }}
                value={getFormFieldValue(USER_FORM_FIELDS.PASSWORD)}
                onBlur={() => validateFormField(USER_FORM_FIELDS.PASSWORD)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? LABELS.HIDE_PASSWORD : LABELS.SHOW_PASSWORD}
                        disabled={isPreRegistrationEnabled}
                        onClick={() => {
                          setShowPassword(showPassword => !showPassword);
                        }}
                      >
                        {showPassword ? <EyeOff fontSize="small" /> : <Eye fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                onChange={(e: any) => updateFormFields('password', e.target.value)}
              />
            </Box>
          </Grid>
          <Grid item xs={4}>
            {defaultPasswordPolicyRule && !isPreRegistrationEnabled && getFormFieldValue(USER_FORM_FIELDS.PASSWORD) && (
              <Box>
                {defaultPasswordPolicyRule?.minLength && defaultPasswordPolicyRule?.maxLength && (
                  <Box className={classes.checkBoxWrap}>
                    {isMinMax ? (
                      <AlertCheckCircleFill style={{ color: LABELS.VALID_COLOR, fontSize: 15 }}></AlertCheckCircleFill>
                    ) : (
                      <ShapeCircle style={{ color: LABELS.DEFAULT_COLOR, fontSize: 15 }}></ShapeCircle>
                    )}
                    <Typography variant="caption" className={classes.policyText}>
                      {interpolateString(
                        [defaultPasswordPolicyRule.minLength, defaultPasswordPolicyRule.maxLength],
                        PASSWORD_POLICY_ARRAY['minMax']
                      )}
                    </Typography>
                  </Box>
                )}
                {defaultPasswordPolicyRule?.includeNumbers && (
                  <Box className={classes.checkBoxWrap}>
                    {isIncludeNumbers ? (
                      <AlertCheckCircleFill style={{ color: LABELS.VALID_COLOR, fontSize: 15 }}></AlertCheckCircleFill>
                    ) : (
                      <ShapeCircle style={{ color: LABELS.DEFAULT_COLOR, fontSize: 15 }}></ShapeCircle>
                    )}
                    <Typography variant="caption" className={classes.policyText}>
                      {PASSWORD_POLICY_ARRAY['includeNumbers']}
                    </Typography>
                  </Box>
                )}
                {defaultPasswordPolicyRule?.includeSpecialCharacters && (
                  <Box className={classes.checkBoxWrap}>
                    {isIncludeSpecialCharacters ? (
                      <AlertCheckCircleFill style={{ color: LABELS.VALID_COLOR, fontSize: 15 }}></AlertCheckCircleFill>
                    ) : (
                      <ShapeCircle style={{ color: LABELS.DEFAULT_COLOR, fontSize: 15 }}></ShapeCircle>
                    )}
                    <Typography variant="caption" className={classes.policyText}>
                      {PASSWORD_POLICY_ARRAY['includeSpecialCharacters']}
                    </Typography>
                  </Box>
                )}
                {defaultPasswordPolicyRule?.lettersInMixedCase && (
                  <Box className={classes.checkBoxWrap}>
                    {isLettersInMixedCase ? (
                      <AlertCheckCircleFill style={{ color: LABELS.VALID_COLOR, fontSize: 15 }}></AlertCheckCircleFill>
                    ) : (
                      <ShapeCircle style={{ color: LABELS.DEFAULT_COLOR, fontSize: 15 }}></ShapeCircle>
                    )}
                    <Typography variant="caption" className={classes.policyText}>
                      {PASSWORD_POLICY_ARRAY['lettersInMixedCase']}
                    </Typography>
                  </Box>
                )}
                {defaultPasswordPolicyRule?.maxConsecutiveLetters && (
                  <Box className={classes.checkBoxWrap}>
                    {isMaxConsecutiveLetters ? (
                      <AlertCheckCircleFill style={{ color: LABELS.VALID_COLOR, fontSize: 15 }}></AlertCheckCircleFill>
                    ) : (
                      <ShapeCircle style={{ color: LABELS.DEFAULT_COLOR, fontSize: 15 }}></ShapeCircle>
                    )}
                    <Typography variant="caption" className={classes.policyText}>
                      {interpolateString(
                        [defaultPasswordPolicyRule.maxConsecutiveLetters],
                        PASSWORD_POLICY_ARRAY['maxConsecutiveLetters']
                      )}
                    </Typography>
                  </Box>
                )}
                {defaultPasswordPolicyRule?.excludePasswordsInDictionary && (
                  <Box className={classes.checkBoxWrap}>
                    {isNotCommonPassword ? (
                      <AlertCheckCircleFill style={{ color: LABELS.VALID_COLOR, fontSize: 15 }}></AlertCheckCircleFill>
                    ) : (
                      <ShapeCircle style={{ color: LABELS.DEFAULT_COLOR, fontSize: 15 }}></ShapeCircle>
                    )}
                    <Typography variant="caption" className={classes.policyText}>
                      {PASSWORD_POLICY_ARRAY['commonPassword']}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Grid>
        </>
      )}
    </Grid>
  );

  const getPhoneInput = () => {
    return (
      <>
        <Box className={classes.modalFormContent}>
          <PhoneInput
            international
            limitMaxLength
            placeholder={LABELS.MOBILE_PHONE}
            id={USER_FORM_FIELDS.MOBILE_PHONE}
            data-testid={USER_FORM_FIELDS.MOBILE_PHONE}
            value={getFormFieldValue(USER_FORM_FIELDS.MOBILE_PHONE)}
            className={`${classes.modalTextBoxPhone} ${getFormFieldErrorStatus(USER_FORM_FIELDS.MOBILE_PHONE) ? classes.modalTextBoxPhoneError : ''
              }`}
            countrySelectProps={{
              unicodeFlags: false,
              arrowComponent: () => {
                return <ArrowTrendDownFill className={classes.countrySelectArrow} />;
              }
            }}
            onChange={value => {
              dispatch(
                updateUserFormDataField(USER_FORM_FIELDS.MOBILE_PHONE, {
                  value: value,
                  error: false,
                  errorHelperText: ''
                })
              );
            }}
          />
        </Box>
        <FormHelperText
          className={classes.mobilePhoneError}
          id={`${USER_FORM_FIELDS.MOBILE_PHONE}-error`}
          error={getFormFieldErrorStatus(USER_FORM_FIELDS.MOBILE_PHONE)}
        >
          {getFormFieldErrorHelperText(USER_FORM_FIELDS.MOBILE_PHONE)}
        </FormHelperText>
      </>
    );
  };

  const getUserContactDetails = () => (
    <Grid container spacing={1} alignItems="flex-end" className={classes.contactDetailsWrap}>
      <Grid item xs={4}>
        <Box className={classes.contentContainer}>
          <TextField
            {...getTextFieldHelperProps({
              required: isLocalUser || (isNativeUser && isPreRegistrationEnabled),
              errorHelperText: getFormFieldErrorHelperText(USER_FORM_FIELDS.EMAIL),
              isNativeUser
            })}
            fullWidth
            id="email"
            name="email"
            data-testid={'email-test-id'}
            label={LABELS.EMAIL}
            variant="standard"
            className={classes.textInput}
            inputProps={{ maxLength: 100 }}
            onBlur={() => validateFormField(USER_FORM_FIELDS.EMAIL)}
            onChange={(e: any) => {
              const { value } = e.target;
              if (emailAsUsername) {
                updateFormFields('userName', value);
                setCopiedEmailAsUsernameValue(value);
              }
              updateFormFields('email', value);
              setEmailAsUsernameInputValue(value);

              if (!value) {
                setEmailAsUsername(false);
              }
            }}
          />
        </Box>
      </Grid>
      {isLocalUser && <Grid item xs={4}>
        <Box className={classes.contentContainer}>{getPhoneInput()}</Box>
      </Grid>}
      {isNativeUser && (
        <Grid item xs={4}>
          <Box className={classes.contentContainer}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  id="emailAsUsername"
                  name="emailAsUsername"
                  data-testid="emailAsUsername"
                  checked={emailAsUsername}
                  disabled={!isValidEmail(getFormFieldValue(USER_FORM_FIELDS.EMAIL).trim())}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const { checked } = event.target;

                    setEmailAsUsername(checked);
                    if (checked) {
                      setCopiedEmailAsUsernameValue(emailAsUsernameInputValue);
                      updateFormFields(USER_FORM_FIELDS.USER_NAME, emailAsUsernameInputValue);
                    } else {
                      setCopiedEmailAsUsernameValue('');
                      dispatch(
                        updateUserFormDataField(USER_FORM_FIELDS.USER_NAME, {
                          value: '',
                          error: false,
                          errorHelperText: ''
                        })
                      );
                    }
                  }}
                />
              }
              label={LABELS.USE_EMAIL_AS_USERNAME}
            />
          </Box>
        </Grid>
      )}
    </Grid>
  );

  const getUserSettingsDetails = () => {
    return (
      <Grid>
        <Grid item xs={12}>
          <Box className={classes.divider}>
            <Divider />
          </Box>
        </Grid>
        <Box className={classes.tabHeader}>{LABELS.SETTINGS}</Box>
        <Box className={classes.contactDetailsWrap}>
          <>
            <Grid item xs={2} className={classes.contentContainer}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    id={USER_FORM_FIELDS.IS_MFA_ENABLED}
                    name={USER_FORM_FIELDS.IS_MFA_ENABLED}
                    data-testid={USER_FORM_FIELDS.IS_MFA_ENABLED}
                    checked={getFormFieldValue(USER_FORM_FIELDS.IS_MFA_ENABLED) ? true : false}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      dispatch(
                        updateUserFormDataField(USER_FORM_FIELDS.IS_MFA_ENABLED, {
                          value: event.target.checked,
                          error: false,
                          errorHelperText: ''
                        })
                      );
                      dispatch(
                        updateUserFormDataField(USER_FORM_FIELDS.PREFERRED_MFA, {
                          value: '',
                          error: false,
                          errorHelperText: ''
                        })
                      );
                      dispatch(
                        updateUserFormDataField(USER_FORM_FIELDS.MOBILE_PHONE, {
                          ...userFormValues[USER_FORM_FIELDS.MOBILE_PHONE],
                          error: false,
                          errorHelperText: ''
                        })
                      );
                    }}
                  />
                }
                label={LABELS.ENABLE_MFA}
              />
            </Grid>

            {getFormFieldValue(USER_FORM_FIELDS.IS_MFA_ENABLED) && (
              <Grid item xs={6} className={classes.contentContainerMFA}>
                <FormControl error={getFormFieldErrorStatus(USER_FORM_FIELDS.PREFERRED_MFA)}>
                  <Box className={classes.dropDownText}>
                    <LuiSelect
                      onSelectionChange={e => {
                        dispatch(
                          updateUserFormDataField(USER_FORM_FIELDS.PREFERRED_MFA, {
                            value: e.target.value.key,
                            error: false,
                            errorHelperText: ''
                          })
                        );
                        if (e.target.value.key === KEY_NAMES.PREFERRED_MFA_EMAIL) {
                          dispatch(
                            updateUserFormDataField(USER_FORM_FIELDS.MOBILE_PHONE, {
                              ...userFormValues[USER_FORM_FIELDS.MOBILE_PHONE],
                              error: false,
                              errorHelperText: ''
                            })
                          );
                        }
                      }}
                      itemData={PREFERRED_MFA_LIST}
                      id={USER_FORM_FIELDS.PREFERRED_MFA}
                      data-testid={USER_FORM_FIELDS.PREFERRED_MFA}
                      inputLabel={LABELS.PREFERRED_MFA}
                      selectedItem={getSelectedItem(
                        getFormFieldValue(USER_FORM_FIELDS.PREFERRED_MFA),
                        'key',
                        PREFERRED_MFA_LIST
                      )}
                      disabled={false}
                      width={400}
                      maxHeight={350}
                      error={getFormFieldErrorStatus(USER_FORM_FIELDS.PREFERRED_MFA)}
                    />
                  </Box>
                </FormControl>
                <FormHelperText
                  id={`${USER_FORM_FIELDS.PREFERRED_MFA}-error`}
                  error={getFormFieldErrorStatus(USER_FORM_FIELDS.PREFERRED_MFA)}
                >
                  {getFormFieldErrorHelperText(USER_FORM_FIELDS.PREFERRED_MFA)}
                </FormHelperText>
              </Grid>
            )}
          </>
        </Box>
      </Grid>
    );
  };

  return (
    <Box className={classes.root}>
      <AppBar elevation={0} position="static" className={classes.appBarWrap}>
        <IconButton
          className={classes.backIcon}
          data-testid="api-header-back-btn"
          onClick={() => {
            if (history.location.search) {
              history.push(`/${history.location.search}`);
            } else {
              history.push('/');
            }
          }}
        >
          <ArrowChevronLeft />
        </IconButton>
        <Box className={classes.headingAlignment}>{LABELS.ADD_USER}</Box>
      </AppBar>
      <Backdrop open={backdropOpen}>
        <CircularProgress color="inherit" size={44} />
      </Backdrop>
      <LuiCard
        title=" "
        elevation={1}
        hasCardContentPadding={true}
        className={classes.cardRoot}

        footerActions={
          <Box className={classes.footerActionsWrap}>
            <Button
              size="medium"
              color="primary"
              variant="outlined"
              data-testid="cancel-btn"
              onClick={() => history.goBack()}
            >
              {LABELS.BUTTON_CANCEL}
            </Button>
            <Button size="medium" color="primary" variant="contained" data-testid="save-btn" onClick={saveUserDetails}>
              {LABELS.BUTTON_SAVE}
            </Button>
          </Box>
        }
      >
        <LuiModal
          modalSize="sm"
          isOpen={openModal}
          primaryAction={
            <Button
              variant={displayCopyPasswordOption ? 'contained' : 'outlined'}
              name={displayCopyPasswordOption ? 'closeBtn' : 'cancelBtn'}
              color="primary"
              disabled={showLoader}
              disableElevation
              onClick={() => {
                handleClose();
                history.goBack();
              }}
            >
              {displayCopyPasswordOption ? LABELS.BUTTON_CLOSE : LABELS.BUTTON_CANCEL}
            </Button>
          }
          handleClose={() => {
            handleClose();
            history.goBack();
          }}
          disableBackdropClick={true}
          disableEscapeKeyDown={true}
          modalTitle={displayCopyPasswordOption ? LABELS.TEMPORARY_PASSWORD : LABELS.ADD_USER}
        >
          {displayCopyPasswordOption && (
            <UserPasswordDisplay
              passwordText={userPasswordText}
              userName={getFormFieldValue(USER_FORM_FIELDS.USER_NAME)}
            />
          )}
        </LuiModal>

        <Grid container spacing={1}>
          <Grid item xs={4}>
            <LuiSelect
              required
              placeholder={LABELS.IDENTITY_PROVIDER}
              disabled={Boolean(locationIdpId)}
              maxHeight={350}
              id={USER_FORM_FIELDS.IDP_ID}
              data-testid={USER_FORM_FIELDS.IDP_ID}
              inputLabel={LABELS.IDENTITY_PROVIDER}
              onSelectionChange={idpTypeChangeHandler}
              itemData={idpList}
              selectedItem={getSelectedItem(selectedIdpId, KEY_NAMES.ID, idpList)}
              error={getFormFieldErrorStatus(USER_FORM_FIELDS.IDP_ID)}
            />
          </Grid>
          <FormHelperText
            id={`${USER_FORM_FIELDS.IDP_ID}-error`}
            error={getFormFieldErrorStatus(USER_FORM_FIELDS.IDP_ID)}
          >
            {getFormFieldErrorHelperText(USER_FORM_FIELDS.IDP_ID)}
          </FormHelperText>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={4}>
            <Box className={classes.contentContainer}>
              <TextField
                {...getTextFieldHelperProps({
                  errorHelperText: getFormFieldErrorHelperText(USER_FORM_FIELDS.GIVEN_NAME),
                  isNativeUser
                })}
                id="firstName"
                name="firstName"
                label={LABELS.FIRST_NAME}
                data-testid={'first-name'}
                className={classes.textInput}
                variant="standard"
                inputProps={{ maxLength: 100 }}
                onChange={(e: any) => updateFormFields('givenName', e.target.value)}
              />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box className={classes.contentContainer}>
              <TextField
                {...getTextFieldHelperProps({
                  errorHelperText: getFormFieldErrorHelperText(USER_FORM_FIELDS.LAST_NAME),
                  isNativeUser
                })}
                id="lastName"
                name="lastName"
                label={LABELS.LAST_NAME}
                variant="standard"
                data-testid={'last-name'}
                className={classes.textInput}
                inputProps={{ maxLength: 100 }}
                onChange={(e: any) => updateFormFields('lastName', e.target.value)}
              />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box className={classes.contentContainer}>
              <TextField
                {...getTextFieldHelperProps({
                  required: isLocalUser,
                  errorHelperText: getFormFieldErrorHelperText(USER_FORM_FIELDS.DISPLAY_NAME),
                  isNativeUser
                })}
                id="displayName"
                name="displayName"
                label={LABELS.DISPLAY_NAME}
                data-testid={'display-name'}
                className={classes.textInput}
                variant="standard"
                inputProps={{ maxLength: 100 }}
                onChange={(e: any) => updateFormFields('displayName', e.target.value)}
              />
            </Box>
          </Grid>
        </Grid>
        {isNativeUser && (
          <Box className={classes.contentContainer}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  id={USER_FORM_FIELDS.PRE_REGISTRATION}
                  name={USER_FORM_FIELDS.PRE_REGISTRATION}
                  data-testid={USER_FORM_FIELDS.PRE_REGISTRATION}
                  checked={isPreRegistrationEnabled}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const { checked } = event.target;

                    dispatch(
                      updateUserFormDataField(USER_FORM_FIELDS.PRE_REGISTRATION, {
                        value: checked,
                        error: false,
                        errorHelperText: ''
                      })
                    );
                    if (checked) {
                      dispatch(
                        updateUserFormDataField(USER_FORM_FIELDS.PASSWORD, {
                          value: '',
                          error: false,
                          errorHelperText: ''
                        })
                      );
                      setShowPassword(false);
                    }
                  }}
                />
              }
              label={
                <>
                  {LABELS.ENABLE_PRE_REGISTRATION}{' '}
                  <Typography variant="body2" color="textSecondary" component="span">
                    ({LABELS.PRE_REGISTRATION_HINT})
                  </Typography>
                </>
              }
            />
          </Box>
        )}
        {isNativeUser ? (
          <>
            {getUserContactDetails()}
            {getUserSignInDetails()}
          </>
        ) : (
          <>
            {getUserSignInDetails()}
            {getUserContactDetails()}
          </>
        )}

        {isLocalUser && getUserSettingsDetails()}

        {errors && <ErrorMessage message={errors.message} />}
        <SnackbarAlert />
      </LuiCard>
    </Box>
  );
};

export default AddUser;
